import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import { captureMessage, addBreadcrumb } from '@/services/telemetry';
import { isFeatureEnabled } from '@/config/featureFlags';

/**
 * Web Vitals thresholds based on Google's recommendations
 * https://web.dev/vitals/
 *
 * Note: FID (First Input Delay) is deprecated in web-vitals v4+
 * and replaced by INP (Interaction to Next Paint)
 */
const THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
};

/**
 * Report a web vital metric
 * @param {Object} metric - Web vital metric object
 */
function reportMetric(metric) {
  const { name, value, rating, id, delta, navigationType } = metric;

  // Add breadcrumb for debugging
  addBreadcrumb({
    category: 'web-vitals',
    message: `${name}: ${value.toFixed(2)}`,
    level: rating === 'poor' ? 'warning' : 'info',
    data: {
      name,
      value,
      rating,
      id,
      delta,
      navigationType,
    },
  });

  // Only send to telemetry if metric is poor
  if (rating === 'poor') {
    captureMessage(`Poor Web Vital: ${name}`, {
      level: 'warning',
      tags: {
        metric: name,
        rating,
        navigationType,
        errorType: 'performance',
      },
      extra: {
        value,
        delta,
        id,
        threshold: THRESHOLDS[name],
      },
    });
  }

  // Log in development
  if (import.meta.env.MODE === 'development') {
    const style =
      rating === 'good'
        ? 'color: green'
        : rating === 'needs-improvement'
          ? 'color: orange'
          : 'color: red';
    console.log(
      `%c[Web Vitals] ${name}: ${value.toFixed(2)} (${rating})`,
      style,
    );
  }
}

/**
 * Initialize Web Vitals monitoring
 * Measures Core Web Vitals and additional metrics
 */
export function initWebVitals() {
  if (!isFeatureEnabled('enableTelemetry')) {
    return;
  }

  // Core Web Vitals (2024 standard)
  onCLS(reportMetric); // Cumulative Layout Shift
  onINP(reportMetric); // Interaction to Next Paint (replaced FID)
  onLCP(reportMetric); // Largest Contentful Paint

  // Additional metrics
  onFCP(reportMetric); // First Contentful Paint
  onTTFB(reportMetric); // Time to First Byte

  if (import.meta.env.MODE === 'development') {
    console.info('[Web Vitals] Monitoring initialized');
  }
}

/**
 * Get all current Web Vitals metrics (for manual reporting)
 * @returns {Promise<Object>} Object containing all metrics
 */
export async function getWebVitals() {
  const metrics = {};

  return new Promise(resolve => {
    let remaining = 5;

    const handleMetric = metric => {
      metrics[metric.name] = {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      };
      remaining--;
      if (remaining === 0) {
        resolve(metrics);
      }
    };

    // Set timeout in case some metrics never fire
    setTimeout(() => resolve(metrics), 5000);

    onCLS(handleMetric, { reportAllChanges: true });
    onFCP(handleMetric);
    onINP(handleMetric, { reportAllChanges: true });
    onLCP(handleMetric, { reportAllChanges: true });
    onTTFB(handleMetric);
  });
}

export default {
  initWebVitals,
  getWebVitals,
  THRESHOLDS,
};
